
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase.ts';
import { User, UserRole } from '../types.ts';
import { Activity, Zap, Clock, ShieldCheck, Key, QrCode, Check, Send, AlertCircle, Copy, CheckCircle2, ArrowRight } from 'lucide-react';
import Footer from './Footer.tsx';

interface SubscriptionViewProps {
    user: User;
    onSignOut: () => void;
}

const SubscriptionView: React.FC<SubscriptionViewProps> = ({ user, onSignOut }) => {
    const [settings, setSettings] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'quarterly' | 'annual' | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasPending, setHasPending] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [settingsRes, pendingRes] = await Promise.all([
                supabase.from('system_settings').select('*').eq('id', 'config').maybeSingle(),
                supabase.from('payment_submissions').select('*').eq('user_id', user.id).eq('status', 'pending').maybeSingle()
            ]);

            if (settingsRes.data) setSettings(settingsRes.data);
            if (pendingRes.data) setHasPending(true);
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const handleNotifyPayment = async () => {
        if (!selectedPlan) return;
        setIsSubmitting(true);
        try {
            const { error } = await supabase.from('payment_submissions').insert([
                { user_id: user.id, plan_type: selectedPlan }
            ]);
            if (error) throw error;
            setHasPending(true);
        } catch (err: any) {
            alert('Erro ao notificar pagamento: ' + err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return (
        <div className="min-h-screen bg-[#0b0e11] flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 animate-pulse">
                <Activity className="text-[#00ff66]" size={48} />
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#00ff66]">Carregando Planos...</p>
            </div>
        </div>
    );

    const plans = [
        {
            type: 'monthly',
            label: 'Plano Mensal',
            price: settings?.price_monthly || 0,
            days: 30,
            icon: <Clock size={24} />,
            color: 'text-[#00ff66]',
            pix: settings?.pix_key_monthly || settings?.pix_key,
            qr: settings?.qr_code_url_monthly || settings?.qr_code_url
        },
        {
            type: 'quarterly',
            label: 'Plano Trimestral',
            price: settings?.price_quarterly || 0,
            days: 90,
            icon: <Zap size={24} />,
            color: 'text-[#fbbf24]',
            pix: settings?.pix_key_quarterly,
            qr: settings?.qr_code_url_quarterly,
            popular: true
        },
        {
            type: 'annual',
            label: 'Plano Anual',
            price: settings?.price_annual || 0,
            days: 365,
            icon: <ShieldCheck size={24} />,
            color: 'text-[#4788ff]',
            pix: settings?.pix_key_annual,
            qr: settings?.qr_code_url_annual
        }
    ];

    if (hasPending) {
        return (
            <div className="min-h-screen bg-[#0b0e11] flex items-center justify-center p-6 bg-radial-gradient">
                <div className="max-w-md w-full bg-[#1e2329] border border-[#474d57] rounded-[40px] p-10 text-center shadow-2xl animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-[#fbbf24]/10 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(251,191,36,0.2)]">
                        <Clock size={40} className="text-[#fbbf24] animate-pulse" />
                    </div>
                    <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-4">Acesso em Processamento</h2>
                    <p className="text-sm text-[#848e9c] font-bold leading-relaxed mb-8">
                        Recebemos sua notificação de pagamento! Nossa equipe está validando os dados. <br /><br />
                        Você receberá acesso total assim que a confirmação for concluída no sistema.
                    </p>
                    <div className="bg-[#0b0e11] border border-[#474d57]/30 rounded-2xl p-4 mb-8">
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#00ff66]">Status: AGUARDANDO APROVAÇÃO</p>
                    </div>
                    <button
                        onClick={onSignOut}
                        className="text-[#848e9c] hover:text-white text-[10px] font-black uppercase tracking-[0.2em] transition-colors"
                    >
                        Sair da Conta
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0b0e11] p-6 lg:p-12 relative overflow-hidden">
            {/* Background Glows */}
            <div className="fixed top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#00ff66]/5 rounded-full blur-[150px] -z-10"></div>

            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row items-center justify-between mb-16 gap-8">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-[#00ff66] p-1.5 rounded-lg shadow-[0_0_20px_rgba(0,255,102,0.5)]">
                                <Activity className="text-black" size={22} strokeWidth={3} />
                            </div>
                            <h1 className="text-xl font-black tracking-tighter uppercase text-white">FIFA<span className="text-[#00ff66]">LYTICS</span></h1>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter leading-none mb-4">
                            ESCOLHA SEU <span className="text-[#00ff66]">PLANO</span>
                        </h2>
                        <p className="text-[#848e9c] font-bold text-sm">Libere agora as ferramentas profissionais do FifaLytics Pro.</p>
                    </div>
                    <button
                        onClick={onSignOut}
                        className="bg-[#1e2329] border border-[#474d57] text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#2b3139] transition-all"
                    >
                        Trocar de Conta
                    </button>
                </div>

                {!selectedPlan ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                        {plans.map((plan) => (
                            <div
                                key={plan.type}
                                onClick={() => setSelectedPlan(plan.type as any)}
                                className={`group relative bg-[#1e2329] border-2 rounded-[48px] p-8 md:p-12 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] ${plan.popular ? 'border-[#00ff66] shadow-[0_0_40px_rgba(0,255,102,0.15)]' : 'border-[#474d57]/30 hover:border-[#00ff66]/50'
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#00ff66] text-[#0b0e11] text-[10px] font-black px-6 py-2 rounded-full uppercase tracking-widest shadow-lg">
                                        Mais Popular
                                    </div>
                                )}

                                <div className={`w-16 h-16 rounded-3xl bg-[#0b0e11] border border-[#474d57]/30 flex items-center justify-center mb-8 ${plan.color} group-hover:scale-110 transition-transform`}>
                                    {plan.icon}
                                </div>

                                <h3 className="text-2xl font-black uppercase italic text-white mb-2">{plan.label}</h3>
                                <p className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest mb-8">{plan.days} Dias de Acesso Pro</p>

                                <div className="flex items-baseline gap-2 mb-10">
                                    <span className="text-4xl font-black text-white italic">R$ {plan.price.toFixed(2).replace('.', ',')}</span>
                                    <span className="text-xs font-bold text-[#848e9c]">/período</span>
                                </div>

                                <ul className="space-y-4 mb-10">
                                    {[
                                        'Monitoramento Real-time',
                                        'Análise de ROI de Players',
                                        'Histórico Completo',
                                        'Filtros Avançados'
                                    ].map((feat, i) => (
                                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-[#eaecef]">
                                            <CheckCircle2 size={16} className="text-[#00ff66]" /> {feat}
                                        </li>
                                    ))}
                                </ul>

                                <div className="w-full bg-[#0b0e11] border border-[#474d57]/30 py-4 rounded-2xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#00ff66] group-hover:bg-[#00ff66] group-hover:text-[#0b0e11] transition-all">
                                    Selecionar Plano <ArrowRight size={14} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="max-w-4xl mx-auto bg-[#1e2329] border-2 border-[#00ff66] rounded-[48px] overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="grid grid-cols-1 md:grid-cols-2">
                            <div className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-[#474d57]/30">
                                <button
                                    onClick={() => setSelectedPlan(null)}
                                    className="flex items-center gap-2 text-[#848e9c] hover:text-white text-[10px] font-black uppercase tracking-widest mb-10 transition-colors"
                                >
                                    <ArrowRight size={14} className="rotate-180" /> Voltar aos Planos
                                </button>

                                <h3 className="text-[10px] font-black text-[#00ff66] uppercase tracking-[0.3em] mb-4">Pagamento via PIX</h3>
                                <h4 className="text-3xl font-black uppercase italic text-white leading-none mb-8">
                                    SCANEIE O <span className="text-[#00ff66]">QR CODE</span>
                                </h4>

                                <div className="bg-white p-6 rounded-3xl mb-8 w-full aspect-square max-w-[280px] mx-auto overflow-hidden shadow-2xl">
                                    {plans.find(p => p.type === selectedPlan)?.qr ? (
                                        <img src={plans.find(p => p.type === selectedPlan)?.qr} alt="QR Code PIX" className="w-full h-full object-contain" />
                                    ) : (
                                        <div className="w-full h-full bg-[#0b0e11] flex items-center justify-center text-white/10">
                                            <QrCode size={64} />
                                        </div>
                                    )}
                                </div>

                                <div className="text-center">
                                    <p className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest">Valor a Transferir</p>
                                    <p className="text-4xl font-black text-[#00ff66] italic mt-2">R$ {plans.find(p => p.type === selectedPlan)?.price.toFixed(2).replace('.', ',')}</p>
                                </div>
                            </div>

                            <div className="p-8 md:p-12 bg-[#0b0e11]/50 backdrop-blur-xl flex flex-col justify-between">
                                <div className="space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest ml-1">Copia e Cola (Chave Pix)</label>
                                        <div className="flex gap-2">
                                            <div className="flex-1 bg-[#0b0e11] border border-[#474d57] rounded-2xl px-6 py-5 text-sm font-mono font-bold text-white truncate">
                                                {plans.find(p => p.type === selectedPlan)?.pix || 'NÃO CONFIGURADO'}
                                            </div>
                                            <button
                                                onClick={() => handleCopy(plans.find(p => p.type === selectedPlan)?.pix || '')}
                                                className="bg-[#00ff66] text-[#0b0e11] p-5 rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-lg"
                                            >
                                                {copySuccess ? <Check size={20} /> : <Copy size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-[#fbbf24]/10 border border-[#fbbf24]/20 p-6 rounded-3xl flex items-start gap-4">
                                        <AlertCircle className="text-[#fbbf24] shrink-0" size={20} />
                                        <p className="text-xs font-bold text-[#fbbf24] leading-relaxed">
                                            Após realizar a transferência, clique no botão abaixo para que possamos liberar seu acesso manualmente no sistema.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-12 space-y-6">
                                    <button
                                        onClick={handleNotifyPayment}
                                        disabled={isSubmitting}
                                        className="w-full bg-[#00ff66] text-[#0b0e11] py-6 rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:shadow-[0_0_40px_rgba(0,255,102,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Processando...' : 'JÁ REALIZEI O PAGAMENTO'} <Send size={18} />
                                    </button>
                                    <p className="text-center text-[9px] font-black text-[#848e9c] uppercase tracking-widest">
                                        Liberação em até 30 minutos em horário comercial
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="max-w-7xl mx-auto">
                <Footer />
            </div>
        </div>
    );
};

export default SubscriptionView;
